import {Article, ArticlePart, Keyword, KeywordForAi} from '@/model/Article';
import {isSeoScoreReadActive} from '@/service/seoScoreCommit';
import {stripInvisibleCharacters} from '@/utils/string';

function cleanField(value: string): string {
    return stripInvisibleCharacters(value || '');
}

function assignIfChanged(current: string | undefined, next: string, write: (value: string) => void): void {
    if (current !== next) {
        write(next);
    }
}

function sameStrings(current: string[] | null | undefined, next: string[]): boolean {
    if (!current || current.length !== next.length) {
        return false;
    }
    for (let i = 0; i < next.length; i++) {
        if (current[i] !== next[i]) {
            return false;
        }
    }
    return true;
}

/**
 * Strip invisible Unicode from fields that ship to SERP, export, generate, and keyword chips.
 * A score read must not write. Callers outside a score read still skip the write when the
 * cleaned value is the same, so a fresh array does not invalidate the seoScore watcher.
 */
export function normalizeArticleInvisibleText(article: Article): void {
    if (isSeoScoreReadActive()) {
        return;
    }
    assignIfChanged(article.metaTitle, cleanField(article.metaTitle), (value) => {
        article.metaTitle = value;
    });
    assignIfChanged(article.metaDescription, cleanField(article.metaDescription), (value) => {
        article.metaDescription = value;
    });
    assignIfChanged(article.aiPrompt, cleanField(article.aiPrompt), (value) => {
        article.aiPrompt = value;
    });
    assignIfChanged(article.keyword, cleanField(article.keyword), (value) => {
        article.keyword = value;
    });
    assignIfChanged(article.selectedKeyword, cleanField(article.selectedKeyword), (value) => {
        article.selectedKeyword = value;
    });
    const nextSecondary = (article.secondaryKeywords || []).map((item) => cleanField(item));
    if (!sameStrings(article.secondaryKeywords, nextSecondary)) {
        article.secondaryKeywords = nextSecondary;
    }
    (article.allKeywords || []).forEach((row: Keyword) => {
        assignIfChanged(row.keyword, cleanField(row.keyword || ''), (value) => {
            row.keyword = value;
        });
    });
    const keywordsForAi = article.keywordsForAi;
    if (Array.isArray(keywordsForAi)) {
        keywordsForAi.forEach((row: KeywordForAi) => {
            assignIfChanged(row.keyword, cleanField(row.keyword || ''), (value) => {
                row.keyword = value;
            });
        });
    }
    for (let i = 1; i <= 6; i++) {
        const key = ('h' + i) as keyof Article;
        const blocks = article[key] as ArticlePart['value'];
        if (!Array.isArray(blocks)) {
            continue;
        }
        blocks.forEach((block) => {
            assignIfChanged(block.h, cleanField(block.h), (value) => {
                block.h = value;
            });
            assignIfChanged(block.p, cleanField(block.p), (value) => {
                block.p = value;
            });
        });
    }
    (article.questions || []).forEach((item) => {
        assignIfChanged(item.question, cleanField(item.question), (value) => {
            item.question = value;
        });
        assignIfChanged(item.answer, cleanField(item.answer), (value) => {
            item.answer = value;
        });
    });
    (article.images || []).forEach((image) => {
        assignIfChanged(image.alt, cleanField(image.alt || ''), (value) => {
            image.alt = value;
        });
    });
}
